import { create } from 'zustand';
import type {
  ReportConfig,
  SavedReport,
  ReportBookmark,
  ReportData,
} from '../api/report.api';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ReportView = 'gallery' | 'config' | 'table';
export type GalleryTab = 'templates' | 'bookmarks';

interface ReportState {
  /** Current view in the reports section */
  view: ReportView;

  /** Current tab in the gallery view */
  galleryTab: GalleryTab;

  /** Saved reports list */
  savedReports: SavedReport[];
  savedReportsLoading: boolean;

  /** Bookmarked reports list */
  bookmarks: ReportBookmark[];
  bookmarksLoading: boolean;

  /** Which report type is being configured */
  currentReportType: string | null;

  /** Report config being edited */
  currentConfig: ReportConfig | null;

  /** Generated report data (utilization or board resources) */
  reportData: ReportData | null;
  reportLoading: boolean;
  reportError: string | null;

  // ─── Actions ──────────────────────────────────────────────────────────────

  setView: (view: ReportView) => void;
  setGalleryTab: (tab: GalleryTab) => void;
  setSavedReports: (reports: SavedReport[]) => void;
  setSavedReportsLoading: (loading: boolean) => void;
  setBookmarks: (bookmarks: ReportBookmark[]) => void;
  setBookmarksLoading: (loading: boolean) => void;
  setCurrentReportType: (type: string | null) => void;
  setCurrentConfig: (config: ReportConfig | null) => void;
  setReportData: (data: ReportData | null) => void;
  setReportLoading: (loading: boolean) => void;
  setReportError: (error: string | null) => void;
  clearReport: () => void;
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useReportStore = create<ReportState>((set) => ({
  view: 'gallery',
  galleryTab: 'templates',
  savedReports: [],
  savedReportsLoading: false,
  bookmarks: [],
  bookmarksLoading: false,
  currentReportType: null,
  currentConfig: null,
  reportData: null,
  reportLoading: false,
  reportError: null,

  setView: (view) => set({ view }),
  setGalleryTab: (galleryTab) => set({ galleryTab }),
  setSavedReports: (savedReports) => set({ savedReports }),
  setSavedReportsLoading: (savedReportsLoading) => set({ savedReportsLoading }),
  setBookmarks: (bookmarks) => set({ bookmarks }),
  setBookmarksLoading: (bookmarksLoading) => set({ bookmarksLoading }),
  setCurrentReportType: (currentReportType) => set({ currentReportType }),
  setCurrentConfig: (currentConfig) => set({ currentConfig }),
  setReportData: (reportData) => set({ reportData, reportError: null }),
  setReportLoading: (reportLoading) => set({ reportLoading }),
  setReportError: (reportError) => set({ reportError }),
  clearReport: () =>
    set({
      view: 'gallery',
      reportData: null,
      reportError: null,
      currentConfig: null,
      currentReportType: null,
    }),
}));
